import { useCallback, useMemo } from "react";
import type { TaskListQuery } from "@common/contracts/task.contract";
import type { View } from "@common/types/views/views";
import type { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { useScope } from "@state/scopeContext.lib";
import {
    getTaskListGroupBy,
    type TaskGroupingContext,
    type TaskListCapabilities,
} from "../taskListCapabilities";
import type { TaskPlaceholderFilters } from "../taskPlaceholderFields";
import { taskListQueryToOverlayFilters } from "../taskListOverlayFilters";
import type { TaskWorkspaceData } from "../useTaskWorkspaceData";
import type { TaskBoardTask } from "../types";
import { BoardLayoutToggle } from "@components/views/board/BoardLayoutToggle";
import type { BoardLayout } from "@components/views/board/boardLayoutCache";
import type {
    TaskBoardDisplayChange,
    TaskBoardDisplayState,
} from "./taskBoardCache";
import type { SetTaskFilters } from "./taskBoardColumnBy";
import { TaskBoardDisplayButton } from "./TaskBoardDisplayButton";
import {
    CLEARED_TASK_FILTER_FIELDS,
    hasActiveTaskFilters,
} from "./taskBoardFilters";
import { getTaskBoardLaneBy } from "./taskBoardFields";
import { getTaskSortValue } from "./taskBoardSort";
import type { TaskColumnConfigs } from "./taskCardColumns";
import {
    getTaskViewBoardDisplay,
    type TaskViewBoardDisplay,
    withTaskViewBoardDisplay,
} from "./taskBoardViewDisplay";
import { useTaskBoardCache } from "./useTaskBoardCache";
import type { TaskBoardCollapseState } from "./useTaskBoardCollapse";
import { useTaskBoardDisplay } from "./useTaskBoardDisplay";
import { useTaskCardFieldColumns } from "./useTaskCardFieldColumns";
import type { TaskBoardModel } from "./useTaskBoardModel";

/** The board's settings and what it shows. */
export interface TasksBoardState {
    display: TaskBoardDisplayState;
    model: TaskBoardModel;
    /** The pager's order on Board; null on List, which keeps its own. */
    navigationTaskIds: string[] | null;
    onCollapsedChange: TaskBoardCollapseState["onCollapsedChange"];
    onCardsDropped: TaskBoardCollapseState["onCardsDropped"];
    capabilities: TaskListCapabilities;
    filters: TaskPlaceholderFilters;
    /** Whether a filter narrows the tasks, so an empty board offers Clear filters. */
    hasActiveFilters: boolean;
}

interface UseTasksBoardLayoutArgs {
    capabilities: TaskListCapabilities;
    /** Null while the tasks load. */
    tasks: TaskBoardTask[] | null;
    /** List's filters, grouping and sort, which Board shares. */
    filters: TaskListQuery;
    workspaceData: TaskWorkspaceData;
    setFilters: SetTaskFilters;
    onOverlayFiltersChange: FiltersChangeFn;
    /** Runs on every layout switch; List's row selection doesn't carry over. */
    onLayoutChange: () => void;
    /**
     * The page's full view (never the narrow one beside an open task): its
     * board display, and its visible columns are the card fields.
     */
    view: View;
    /** Updates the full view, as List's column settings do; unsaved until Save. */
    onViewChange: (view: View) => void;
    /** List's column definitions, for the columns its settings offer and their names. */
    columnConfigs: TaskColumnConfigs;
    /** The view picked in the views menu, which keeps its own collapse state; null with none. */
    viewId: string | null;
}

/**
 * The List/Board layout for a Tasks page. With the board flag off the page
 * stays on List, there is no toggle, and nothing is read from storage. The
 * swimlanes and empty columns are the view's, so changing them is an unsaved
 * change; the layout is the user's own, so any view opens in either layout,
 * and so are the collapsed columns and lanes, kept per view (see
 * `useTaskBoardCollapse`). Board columns are List's grouping, its order
 * List's sort and its card fields List's visible columns, so switching keeps
 * all three.
 */
export const useTasksBoardLayout = ({
    capabilities,
    tasks,
    filters,
    workspaceData,
    setFilters,
    onOverlayFiltersChange,
    onLayoutChange,
    view,
    onViewChange,
    columnConfigs,
    viewId,
}: UseTasksBoardLayoutArgs) => {
    const { layout, collapsed, setLayout, updateCollapsed } = useTaskBoardCache(
        { isCrossWorkspace: capabilities.isCrossWorkspace },
    );
    const viewDisplay = useMemo(
        () => getTaskViewBoardDisplay(view.options),
        [view.options],
    );
    const isBoardLayout = layout === "board";
    const { organizationId } = useScope();
    const {
        hasCloseStages,
        hasCloses,
        areCloseStagesLoading,
        areClosesFetched,
        areClosesFetching,
    } = workspaceData;
    const groupingContext = useMemo<TaskGroupingContext>(
        () => ({
            capabilities,
            hasOrganization: !!organizationId,
            hasCloseStages,
            hasCloses,
        }),
        [capabilities, organizationId, hasCloseStages, hasCloses],
    );
    const groupBy = getTaskListGroupBy({
        groupBy: filters.groupBy,
        capabilities,
    });
    const laneBy = getTaskBoardLaneBy({
        ...groupingContext,
        storedLaneBy: viewDisplay.groupBy,
        groupBy,
        isGroupingDataLoading:
            areCloseStagesLoading || (areClosesFetching && !areClosesFetched),
    });
    const cardFields = useTaskCardFieldColumns({
        view,
        onViewChange,
        columnConfigs,
        isCommentsShown: viewDisplay.isCommentsShown,
    });
    const { model, collapse, display } = useTaskBoardDisplay({
        tasks: isBoardLayout ? tasks : null,
        filters,
        viewDisplay,
        viewColumns: view.columns,
        groupBy,
        laneBy,
        workspaceData,
        isCrossWorkspace: capabilities.isCrossWorkspace,
        viewId,
        collapseByViewId: collapsed,
        onCollapseByViewIdChange: updateCollapsed,
    });

    const updateViewDisplay = useCallback(
        (change: Partial<TaskViewBoardDisplay>) =>
            onViewChange({
                ...view,
                options: withTaskViewBoardDisplay({
                    options: view.options,
                    change,
                }),
            }),
        [view, onViewChange],
    );

    /** A sort change writes List's sort; the rest are the view's display. */
    const handleDisplayChange = useCallback(
        ({
            sort,
            sortDirection,
            ...viewDisplayChange
        }: TaskBoardDisplayChange) => {
            if (sort === undefined && sortDirection === undefined) {
                updateViewDisplay(viewDisplayChange);
                return;
            }
            const nextSort = getTaskSortValue(
                { sort, sortDirection },
                filters.sort,
            );
            if (nextSort !== filters.sort) {
                setFilters((prev) => ({ ...prev, sort: nextSort }));
            }
        },
        [filters.sort, setFilters, updateViewDisplay],
    );

    const handleLayoutChange = useCallback(
        (nextLayout: BoardLayout) => {
            onLayoutChange();
            setLayout(nextLayout);
        },
        [onLayoutChange, setLayout],
    );

    /** Clears search, statuses, dates, entities and the filter menu; keeps grouping and sort. */
    const clearFilters = useCallback(() => {
        setFilters((prev) => ({
            ...prev,
            ...CLEARED_TASK_FILTER_FIELDS,
            status: capabilities.defaultStatuses,
        }));
        onOverlayFiltersChange(taskListQueryToOverlayFilters({}));
    }, [setFilters, onOverlayFiltersChange, capabilities.defaultStatuses]);
    const hasActiveFilters = useMemo(
        () =>
            hasActiveTaskFilters({
                filters,
                defaultStatuses: capabilities.defaultStatuses,
            }),
        [filters, capabilities.defaultStatuses],
    );

    // Board's gear takes the slot of List's column settings, so the toggle stays put.
    const toolbarControls = useMemo(
        () => (
            <>
                <BoardLayoutToggle
                    layout={layout}
                    onLayoutChange={handleLayoutChange}
                />
                {isBoardLayout && (
                    <TaskBoardDisplayButton
                        display={display}
                        groupBy={groupBy}
                        laneBy={laneBy}
                        groupingContext={groupingContext}
                        onDisplayChange={handleDisplayChange}
                        cardFields={cardFields}
                    />
                )}
            </>
        ),
        [
            isBoardLayout,
            layout,
            handleLayoutChange,
            display,
            groupBy,
            laneBy,
            groupingContext,
            handleDisplayChange,
            cardFields,
        ],
    );

    const boardState = useMemo<TasksBoardState>(
        () => ({
            display,
            model,
            navigationTaskIds: isBoardLayout ? model.navigationTaskIds : null,
            onCollapsedChange: collapse.onCollapsedChange,
            onCardsDropped: collapse.onCardsDropped,
            capabilities,
            filters,
            hasActiveFilters,
        }),
        [
            display,
            model,
            isBoardLayout,
            collapse.onCollapsedChange,
            collapse.onCardsDropped,
            capabilities,
            filters,
            hasActiveFilters,
        ],
    );

    return {
        isBoardLayout,
        board: boardState,
        toolbarControls,
        clearFilters,
    };
};
